import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Class, Enrollment } from "@/types/classTypes";
import EnrollmentWithPaymentDialog from "./EnrollmentWithPaymentDialog";
import { EditClassDialog } from "./EditClassDialog";
import { toast } from "sonner";
import ClassTabs from "./ClassTabs";
import { useDataServices } from "@/hooks/useDataServices";
import ClassDetailHeader from "./class-detail/ClassDetailHeader";
import ClassDetailActions from "./class-detail/ClassDetailActions";
import ClassDetailLoading from "./class-detail/ClassDetailLoading";
import ClassDetailError from "./class-detail/ClassDetailError";
import { useOrganizationCoreSettings } from '@/hooks/useOrganizationCoreSettings';

interface ClassDetailProps {
  classId: string;
  organizationId: string;
  initialData?: Class | null;
  loading?: boolean;
  error?: Error | null;
}

const ClassDetail: React.FC<ClassDetailProps> = ({ 
  classId, 
  organizationId,
  initialData,
  loading: initialLoading = false,
  error: initialError = null
}) => {
  const navigate = useNavigate();
  const { class: classService } = useDataServices();
  const { organization } = useOrganizationCoreSettings(organizationId);
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [classData, setClassData] = useState<Class | null>(initialData || null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState<Error | null>(initialError);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!classId) return;
      
      try {
        setLoading(true);
        const data = await classService.getClassById(classId);
        setClassData(data);
      } catch (err) {
        console.error("Error fetching class details:", err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    const fetchEnrollments = async () => {
      if (!classId) return;
      
      try {
        setEnrollmentsLoading(true);
        const data = await classService.getClassEnrollments(classId);
        setEnrollments(data);
      } catch (err) {
        console.error("Error fetching enrollments:", err);
      } finally {
        setEnrollmentsLoading(false);
      }
    };

    if (!initialData) {
      fetchData();
    } else {
      setClassData(initialData);
    }
    
    fetchEnrollments();
  }, [classId, classService, initialData]);

  const handleDeleteClass = async () => {
    if (!classData) return;
    
    if (!confirm(`Are you sure you want to delete this ${classData?.is_event ? 'event' : 'class'}? This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    
    try {
      const success = await classService.deleteClass(classId);
      if (success) {
        toast.success(`${classData?.is_event ? 'Event' : 'Class'} deleted successfully`);
        navigate("/admin/classes");
      }
    } catch (error) {
      console.error(`Error deleting ${classData?.is_event ? 'event' : 'class'}:`, error);
      toast.error(`Failed to delete ${classData?.is_event ? 'event' : 'class'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEnrollmentComplete = async () => {
    setIsEnrollDialogOpen(false);
    try {
      setEnrollmentsLoading(true);
      const data = await classService.getClassEnrollments(classId);
      setEnrollments(data);
    } catch (err) {
      console.error("Error refreshing enrollments:", err);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const handleScheduleUpdated = async () => {
    try {
      setLoading(true);
      const data = await classService.getClassById(classId);
      setClassData(data);
    } catch (err) {
      console.error("Error refreshing class schedules:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <ClassDetailLoading />;
  }

  if (error || !classData) {
    return <ClassDetailError isEvent={classData?.is_event} />;
  }

  return (
    <div className="space-y-4">
      <ClassDetailActions 
        isEvent={classData.is_event || false} 
        onAddStudent={() => setIsEnrollDialogOpen(true)} 
      />

      <Card>
        <CardContent className="pt-6">
          <ClassDetailHeader 
            classData={classData}
            onEdit={() => navigate(`/classes/${classId}/edit`)}
            onDelete={handleDeleteClass}
            isDeleting={isDeleting}
            parentCanSelfEnrollTrial={organization?.parent_can_self_enroll_trial}
          />
          <Separator className="mb-4" />
          
          <ClassTabs 
            classData={classData} 
            enrollments={enrollments} 
            organizationId={organizationId}
            onScheduleUpdated={handleScheduleUpdated}
          />
        </CardContent>
      </Card>

      {isEnrollDialogOpen && classData && (
        <EnrollmentWithPaymentDialog
          open={isEnrollDialogOpen}
          onOpenChange={() => setIsEnrollDialogOpen(false)}
          classData={{
            id: classData.id,
            name: classData.name,
            price_cents: classData.price_cents || 0,
            billing_model: classData.billing_model || 'monthly',
            payment_policy: classData.payment_policy || 'immediate',
            payment_deadline_days: classData.payment_deadline_days || 7,
            max_students: classData.max_students
          }}
          organizationId={organizationId}
          onEnrollmentSuccess={handleEnrollmentComplete}
          currentEnrollmentCount={enrollments.filter(e => e.status === 'active').length}
        />
      )}
    </div>
  );
};

export default ClassDetail;
